import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  Image,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Animated,
  PanResponder,
  Dimensions,
  NativeSyntheticEvent,
  NativeScrollEvent,
  LayoutChangeEvent,
} from 'react-native';
import { createStyles, SCREEN_DIMENSIONS } from './DbImagePreview.styles';
import type { DbImagePreviewProps, DbImageGalleryProps, ImageItem } from './DbImagePreview.types';

// 解析图片
const parseImage = (image: string | ImageItem): ImageItem => {
  if (typeof image === 'string') {
    return { url: image };
  }
  return image;
};

// 图片预览组件
const DbImagePreview: React.FC<DbImagePreviewProps> = ({
  images,
  visible,
  initialIndex = 0,
  onClose,
  onChange,
  onImagePress,
  onImageLongPress,
  showIndicator = true,
  indicatorType = 'dot',
  showCloseButton = true,
  showSaveButton = false,
  onSave,
  enableZoom = true,
  minZoom = 1,
  maxZoom = 3,
  loop = false,
  backgroundColor = '#000000',
  backgroundOpacity = 1,
  animationDuration = 200,
  style,
  imageStyle,
  indicatorStyle,
  renderHeader,
  renderFooter,
  renderIndicator,
  loadingComponent,
  errorComponent,
}) => {
  const styles = useMemo(() => createStyles('light'), []);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loadingStates, setLoadingStates] = useState<Record<number, boolean>>({});
  const [errorStates, setErrorStates] = useState<Record<number, boolean>>({});
  const scrollViewRef = useRef<ScrollView>(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const parsedImages = useMemo(() => images.map(parseImage), [images]);

  // 显示/隐藏动画
  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      // 滚动到初始位置
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({
          x: initialIndex * SCREEN_DIMENSIONS.width,
          animated: false,
        });
      }, 0);
      
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: animationDuration,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: animationDuration,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, initialIndex, animationDuration, fadeAnim]);

  // 滚动结束
  const handleScrollEnd = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetX = event.nativeEvent.contentOffset.x;
    const newIndex = Math.round(offsetX / SCREEN_DIMENSIONS.width);
    
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < parsedImages.length) {
      setCurrentIndex(newIndex);
      onChange?.(newIndex);
    }
  }, [currentIndex, parsedImages.length, onChange]);

  // 图片加载开始
  const handleLoadStart = useCallback((index: number) => {
    setLoadingStates(prev => ({ ...prev, [index]: true }));
  }, []);

  // 图片加载成功
  const handleLoadEnd = useCallback((index: number) => {
    setLoadingStates(prev => ({ ...prev, [index]: false }));
  }, []);

  // 图片加载失败
  const handleError = useCallback((index: number) => {
    setLoadingStates(prev => ({ ...prev, [index]: false }));
    setErrorStates(prev => ({ ...prev, [index]: true }));
  }, []);

  // 渲染指示器
  const renderIndicatorComponent = () => {
    if (!showIndicator) return null;
    if (renderIndicator) {
      return renderIndicator(currentIndex, parsedImages.length);
    }

    if (indicatorType === 'number' || indicatorType === 'fraction') {
      return (
        <View style={[styles.indicatorNumber, indicatorStyle]}>
          <Text style={styles.indicatorNumberText}>
            {currentIndex + 1} / {parsedImages.length}
          </Text>
        </View>
      );
    }

    // Dot indicator
    return (
      <View style={[styles.indicatorContainer, indicatorStyle]}>
        {parsedImages.map((_, index) => (
          <View
            key={index}
            style={[
              styles.indicatorDot,
              index === currentIndex && styles.indicatorDotActive,
            ]}
          />
        ))}
      </View>
    );
  };

  // 渲染图片
  const renderImage = (item: ImageItem, index: number) => {
    const isLoading = loadingStates[index];
    const hasError = errorStates[index];

    return (
      <View key={index} style={styles.imageContainer}>
        <TouchableOpacity
          activeOpacity={1}
          onPress={() => onImagePress?.(index)}
          onLongPress={() => onImageLongPress?.(index)}
        >
          <Image
            source={{ uri: item.url }}
            style={[styles.image, imageStyle]}
            onLoadStart={() => handleLoadStart(index)}
            onLoadEnd={() => handleLoadEnd(index)}
            onError={() => handleError(index)}
          />
        </TouchableOpacity>

        {isLoading && (
          <View style={styles.loadingContainer}>
            {loadingComponent || (
              <>
                <ActivityIndicator size="large" color="#FFFFFF" />
                <Text style={styles.loadingText}>加载中...</Text>
              </>
            )}
          </View>
        )}

        {hasError && (
          <View style={styles.errorContainer}>
            {errorComponent || (
              <>
                <Text style={styles.errorIcon}>✕</Text>
                <Text style={styles.errorText}>加载失败</Text>
              </>
            )}
          </View>
        )}
      </View>
    );
  };

  // 渲染保存按钮
  const handleSave = useCallback(() => {
    onSave?.(currentIndex, parsedImages[currentIndex]);
  }, [currentIndex, parsedImages, onSave]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Animated.View
        style={[
          styles.container,
          {
            backgroundColor,
            opacity: fadeAnim,
          },
          style,
        ]}
      >
        {/* Header */}
        <View style={styles.header}>
          {showCloseButton && (
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Text style={styles.closeIcon}>×</Text>
            </TouchableOpacity>
          )}
          
          {renderHeader?.(currentIndex)}
          
          <View style={styles.headerRight}>
            {showSaveButton && (
              <TouchableOpacity style={styles.actionButton} onPress={handleSave}>
                <Text style={styles.actionIcon}>↓</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Images */}
        <ScrollView
          ref={scrollViewRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onMomentumScrollEnd={handleScrollEnd}
          style={styles.scrollView}
        >
          {parsedImages.map((item, index) => renderImage(item, index))}
        </ScrollView>

        {/* Footer */}
        <View style={styles.footer}>
          {parsedImages[currentIndex]?.description && (
            <Text style={styles.description}>
              {parsedImages[currentIndex].description}
            </Text>
          )}
          
          {renderIndicatorComponent()}
          {renderFooter?.(currentIndex)}
        </View>
      </Animated.View>
    </Modal>
  );
};

// 图片画廊组件
export const DbImageGallery: React.FC<DbImageGalleryProps> = ({
  images,
  columns = 3,
  gap = 4,
  imageRadius = 4,
  imageHeight,
  style,
  imageStyle,
  previewOnPress = true,
  onImagePress,
  maxDisplay,
  overflowText = (count) => `+${count}`,
}) => {
  const styles = useMemo(() => createStyles('light'), []);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const parsedImages = useMemo(() => images.map(parseImage), [images]);
  const displayImages = useMemo(() => {
    if (maxDisplay && parsedImages.length > maxDisplay) {
      return parsedImages.slice(0, maxDisplay);
    }
    return parsedImages;
  }, [parsedImages, maxDisplay]);

  const overflowCount = useMemo(() => {
    if (maxDisplay && parsedImages.length > maxDisplay) {
      return parsedImages.length - maxDisplay;
    }
    return 0;
  }, [parsedImages.length, maxDisplay]);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const imageWidth = useMemo(() => {
    if (containerWidth === 0) return 100;
    return (containerWidth - gap * (columns - 1)) / columns;
  }, [containerWidth, columns, gap]);

  const calculatedHeight = imageHeight || imageWidth;

  const handleImagePress = useCallback((index: number) => {
    onImagePress?.(index);
    if (previewOnPress) {
      setPreviewIndex(index);
      setPreviewVisible(true);
    }
  }, [previewOnPress, onImagePress]);

  const handleClosePreview = useCallback(() => {
    setPreviewVisible(false);
  }, []);

  return (
    <>
      <View
        style={[styles.galleryContainer, { gap }, style]}
        onLayout={handleLayout}
      >
        {displayImages.map((item, index) => {
          const isLast = index === displayImages.length - 1 && overflowCount > 0;
          
          return (
            <TouchableOpacity
              key={index}
              activeOpacity={0.8}
              onPress={() => handleImagePress(index)}
            >
              <Image
                source={{ uri: item.thumbnail || item.url }}
                style={[
                  styles.galleryImage,
                  {
                    width: imageWidth,
                    height: calculatedHeight,
                    borderRadius: imageRadius,
                  },
                  imageStyle,
                ]}
              />
              {isLast && (
                <View style={[styles.galleryOverflow, { borderRadius: imageRadius }]}>
                  <Text style={styles.galleryOverflowText}>
                    {overflowText(overflowCount)}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      <DbImagePreview
        images={parsedImages}
        visible={previewVisible}
        initialIndex={previewIndex}
        onClose={handleClosePreview}
      />
    </>
  );
};

export default DbImagePreview;
export type { DbImagePreviewProps, DbImageGalleryProps, ImageItem };
